/**
 * Alshallal Pastry Company HR System - My Documents Page
 * Phase 3: Document Management
 * Phase 25.1: Enhanced with Document Preview component
 * Employee view of their uploaded documents
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Upload,
  FileText,
  Eye,
  Trash2,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Plus,
  AlertCircle,
  Loader2,
  Calendar,
  File
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import DocumentPreview from '@/components/documents/DocumentPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle },
};

const STATUS_LABELS = {
  pending: { en: 'Pending Review', ar: 'قيد المراجعة' },
  approved: { en: 'Approved', ar: 'موافق عليه' },
  rejected: { en: 'Rejected', ar: 'مرفوض' },
};

const MyDocumentsPage = () => {
  const { token, user } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Redirect ALL users to appropriate location
  // - Employees: View documents in My Profile
  // - HR/Admin: Manage documents via Employee Profile
  useEffect(() => {
    if (user?.role === 'employee') {
      navigate('/my-profile', { replace: true });
    } else {
      // HR/Admin should manage documents via Employee Profile
      navigate('/employees', { replace: true });
    }
  }, [user, navigate]);

  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upload dialog
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    document_type_id: '',
    issue_date: '',
    expiry_date: '',
    notes: '',
    file: null
  });

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  // View dialog
  const [viewDialog, setViewDialog] = useState({ open: false, document: null });

  // Fetch document types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`${API_URL}/documents/types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDocumentTypes(response.data.document_types || []);
      } catch (err) {
        console.error('Failed to fetch document types:', err);
      }
    };
    fetchTypes();
  }, [token]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/documents/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.document_type_id) return;

    try {
      setUploading(true);
      setError(null);

      // Get employee ID first
      const empResponse = await axios.get(`${API_URL}/employees/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page_size: 1 }
      });

      // For employees, find their own record
      let employeeId = null;
      if (empResponse.data.employees?.length > 0) {
        // This should work for HR viewing their own linked employee
        const meResponse = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Find employee linked to current user
        const allEmps = await axios.get(`${API_URL}/employees/?page_size=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const myEmployee = allEmps.data.employees?.find(e => e.user_id === meResponse.data.id);
        if (myEmployee) {
          employeeId = myEmployee.id;
        } else {
          // Use first employee for demo (HR uploading for employees)
          employeeId = empResponse.data.employees[0].id;
        }
      }

      if (!employeeId) {
        throw new Error('No employee record found');
      }

      const formData = new FormData();
      formData.append('file', uploadForm.file);

      const params = new URLSearchParams();
      params.append('document_type_id', uploadForm.document_type_id);
      if (uploadForm.issue_date) params.append('issue_date', uploadForm.issue_date);
      if (uploadForm.expiry_date) params.append('expiry_date', uploadForm.expiry_date);
      if (uploadForm.notes) params.append('notes', uploadForm.notes);

      await axios.post(
        `${API_URL}/documents/upload/${employeeId}?${params.toString()}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUploadDialog(false);
      setUploadForm({ document_type_id: '', issue_date: '', expiry_date: '', notes: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.document || deleteReason.length < 10) return;

    try {
      setDeleting(true);
      await axios.delete(
        `${API_URL}/documents/${deleteDialog.document.id}?reason=${encodeURIComponent(deleteReason)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteDialog({ open: false, document: null });
      setDeleteReason('');
      fetchDocuments();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.response?.data?.detail?.error || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await axios.get(
        `${API_URL}/documents/${document.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement ? window.document.createElement('a') : null;
      if (link) {
        link.href = url;
        link.setAttribute('download', document.file_name);
        window.document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed');
    }
  };

  const getStatusLabel = (status) => {
    const labels = STATUS_LABELS[status];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : status;
  };

  const getSelectedDocType = () => {
    return documentTypes.find(t => t.id === uploadForm.document_type_id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="my-documents-page">
      {/* Header - responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            {isRTL ? 'مستنداتي' : 'My Documents'}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            {user?.role === 'employee' 
              ? (isRTL ? 'عرض المستندات الشخصية' : 'View your personal documents')
              : (isRTL ? 'إدارة وتحميل المستندات الشخصية' : 'Manage and upload personal documents')
            }
          </p>
        </div>
        {/* Upload button hidden for employees - they can only upload via request attachments */}
        {user?.role !== 'employee' && (
          <Button onClick={() => setUploadDialog(true)} data-testid="upload-btn" className="w-full sm:w-auto">
            <Upload className="h-4 w-4 me-2" />
            {isRTL ? 'رفع مستند' : 'Upload Document'}
          </Button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Documents Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {isRTL ? 'لا توجد مستندات' : 'No Documents'}
            </h3>
            <p className="text-slate-600 mb-4">
              {user?.role === 'employee' 
                ? (isRTL ? 'لا توجد مستندات مرفوعة من قسم الموارد البشرية' : 'No documents uploaded by HR yet')
                : (isRTL ? 'لم تقم برفع أي مستندات بعد' : 'You haven\'t uploaded any documents yet')
              }
            </p>
            {user?.role !== 'employee' && (
              <Button onClick={() => setUploadDialog(true)}>
                <Plus className="h-4 w-4 me-2" />
                {isRTL ? 'رفع أول مستند' : 'Upload First Document'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {documents.map((doc) => {
            const StatusIcon = STATUS_STYLES[doc.status]?.icon || Clock;
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <File className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 line-clamp-1">
                          {doc.document_type_name || 'Document'}
                        </p>
                        <p className="text-sm text-slate-500 line-clamp-1">
                          {doc.file_name}
                        </p>
                      </div>
                    </div>
                    <Badge className={STATUS_STYLES[doc.status]?.bg}>
                      <StatusIcon className="h-3 w-3 me-1" />
                      {getStatusLabel(doc.status)}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-slate-500">{isRTL ? 'تاريخ الرفع' : 'Uploaded'}</span>
                      <span className="text-slate-700">{formatDate(doc.uploaded_at)}</span>
                    </div>
                    {doc.expiry_date && (
                      <div className="flex flex-col gap-1">
                        <div className="flex justify-between">
                          <span className="text-slate-500">{isRTL ? 'تاريخ الانتهاء' : 'Expires'}</span>
                          <span className={cn(
                            "text-slate-700",
                            doc.is_expired && "text-red-600 font-medium"
                          )}>
                            {formatDate(doc.expiry_date)}
                          </span>
                        </div>
                        {/* Phase 19: Expiry countdown badge */}
                        {doc.days_until_expiry !== null && doc.days_until_expiry !== undefined && (
                          <div className="flex justify-end">
                            <Badge 
                              variant="outline"
                              className={cn(
                                "text-xs",
                                doc.days_until_expiry < 0 && "bg-red-100 text-red-800 border-red-200",
                                doc.days_until_expiry >= 0 && doc.days_until_expiry <= 7 && "bg-orange-100 text-orange-800 border-orange-200",
                                doc.days_until_expiry > 7 && doc.days_until_expiry <= 30 && "bg-amber-100 text-amber-800 border-amber-200",
                                doc.days_until_expiry > 30 && "bg-green-100 text-green-800 border-green-200"
                              )}
                            >
                              {doc.days_until_expiry < 0 
                                ? (isRTL ? `منتهية منذ ${Math.abs(doc.days_until_expiry)} يوم` : `Expired ${Math.abs(doc.days_until_expiry)} days ago`)
                                : doc.days_until_expiry === 0
                                  ? (isRTL ? 'تنتهي اليوم' : 'Expires today')
                                  : (isRTL ? `${doc.days_until_expiry} يوم متبقي` : `${doc.days_until_expiry} days left`)
                              }
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}
                    {!doc.expiry_date && doc.requires_expiry && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">{isRTL ? 'تاريخ الانتهاء' : 'Expires'}</span>
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs gap-1" data-testid={`no-expiry-badge-${doc.id}`}>
                          <AlertCircle className="h-3 w-3" />
                          {isRTL ? 'لا يوجد تاريخ انتهاء' : 'No expiry set'}
                        </Badge>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">{isRTL ? 'الحجم' : 'Size'}</span>
                      <span className="text-slate-700">{formatFileSize(doc.file_size)}</span>
                    </div>
                  </div>

                  {/* Rejection reason */}
                  {doc.status === 'rejected' && doc.review_reason && (
                    <div className="p-2 bg-red-50 rounded text-sm text-red-700 mb-4">
                      <span className="font-medium">{isRTL ? 'سبب الرفض:' : 'Rejection reason:'}</span> {doc.review_reason}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setViewDialog({ open: true, document: doc })}
                    >
                      <Eye className="h-4 w-4 me-1" />
                      {t('view')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {doc.status === 'pending' && doc.document_class !== 'nda' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteDialog({ open: true, document: doc })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'رفع مستند جديد' : 'Upload New Document'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'اختر نوع المستند وارفع الملف' : 'Select document type and upload file'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Document Type */}
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع المستند' : 'Document Type'} *</Label>
              <Select
                value={uploadForm.document_type_id}
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, document_type_id: v }))}
              >
                <SelectTrigger data-testid="doc-type-select">
                  <SelectValue placeholder={isRTL ? 'اختر نوع المستند' : 'Select document type'} />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Input */}
            <div className="space-y-2">
              <Label>{isRTL ? 'الملف' : 'File'} *</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileSelect}
                data-testid="file-input"
              />
              <p className="text-xs text-slate-500">
                {isRTL ? 'PDF, JPG, PNG, DOC (الحد الأقصى 10 ميجابايت)' : 'PDF, JPG, PNG, DOC (max 10 MB)'}
              </p>
            </div>

            {/* Issue Date */}
            <div className="space-y-2">
              <Label>{isRTL ? 'تاريخ الإصدار' : 'Issue Date'}</Label>
              <Input
                type="date"
                value={uploadForm.issue_date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, issue_date: e.target.value }))}
              />
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
              <Label>
                {isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}
              </Label>
              <Input
                type="date"
                value={uploadForm.expiry_date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                value={uploadForm.notes}
                onChange={(e) => setUploadForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={isRTL ? 'ملاحظات اختيارية...' : 'Optional notes...'}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!uploadForm.file || !uploadForm.document_type_id || uploading}
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {isRTL ? 'جاري الرفع...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 me-2" />
                  {isRTL ? 'رفع' : 'Upload'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      {/* Phase 25.1: Document Preview */}
      <DocumentPreview
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, document: null })}
        document={viewDialog.document}
        token={token}
        isRTL={isRTL}
        documentType="employee"
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, document: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {isRTL ? 'حذف المستند' : 'Delete Document'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? 'هل أنت متأكد من حذف هذا المستند؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to delete this document? This action cannot be undone.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'سبب الحذف (مطلوب)' : 'Reason for deletion (required)'}</Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب الحذف (10 أحرف على الأقل)...' : 'Enter reason (at least 10 characters)...'}
                rows={3}
              />
              {deleteReason.length > 0 && deleteReason.length < 10 && (
                <p className="text-sm text-red-500">
                  {isRTL ? `${10 - deleteReason.length} أحرف متبقية` : `${10 - deleteReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, document: null })}>
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteReason.length < 10 || deleting}
            >
              {deleting ? t('loading') : t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyDocumentsPage;
